import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Card,
  CardContent,
  Stack,
  Fade,
  Container,
  alpha,
  Autocomplete,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { RootState } from '../../../store/store';
import { useLazyGetRolesForPageQuery, useAddRoleMutation, useUpdateRoleMutation, useLazyRemoveRoleQuery } from '../../../components/services/services';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import FintechLoader from '../../../components/FintechLoader';
import WaveAnimation from '../../../components/WaveAnimation';
import { useMultiplePermissions } from '../../../hooks/usePermission';
import RoleActions from './actions/RoleActions';

export default function AddRole() {
  const { currentUserState } = useSelector((state: RootState) => state?.currentUserState);
  const { enqueueSnackbar } = useSnackbar();

  const permissions = useMultiplePermissions([
    'Corporate.POST.Updating.RolGüncellemeYetkisi',
    'Corporate.GET.Deleting.RolSilmeYetkisi',
    'Corporate.POST.Writing.RolTanımlamaYetkisi',
    'Corporate.GET.Reading.RolListesineErişim'
  ]);

  const canUpdateRole = permissions['Corporate.POST.Updating.RolGüncellemeYetkisi']?.hasPermission || false;
  const canDeleteRole = permissions['Corporate.GET.Deleting.RolSilmeYetkisi']?.hasPermission || false;
  const canCreateRole = permissions['Corporate.POST.Writing.RolTanımlamaYetkisi']?.hasPermission || false;
  const canViewRoleList = permissions['Corporate.GET.Reading.RolListesineErişim']?.hasPermission || false;

  const isLoadingPermissions = permissions['Corporate.POST.Updating.RolGüncellemeYetkisi']?.isLoading ||
    permissions['Corporate.GET.Deleting.RolSilmeYetkisi']?.isLoading ||
    permissions['Corporate.POST.Writing.RolTanımlamaYetkisi']?.isLoading ||
    permissions['Corporate.GET.Reading.RolListesineErişim']?.isLoading;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [isChecker, setIsChecker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [roleNameError, setRoleNameError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const [editingRole, setEditingRole] = useState<any>(null);
  const [editRoleName, setEditRoleName] = useState('');
  const [editIsChecker, setEditIsChecker] = useState(false);
  const [editIsMaker, setEditIsMaker] = useState(false);
  const [editApprovalRoles, setEditApprovalRoles] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<any>(null);

  const [getRolesForPageTrigger, { data: rolesData, isLoading: rolesLoading, isUninitialized: rolesUninitialized }] = useLazyGetRolesForPageQuery();
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [removeRoleTrigger] = useLazyRemoveRoleQuery();

  const isLoading = isLoadingPermissions || rolesLoading || rolesUninitialized || loading || deleteLoading;
  const hasFetchedRoles = !rolesUninitialized && rolesData !== undefined;

  useEffect(() => {
    if (currentUserState?.companyId && canViewRoleList && !isLoadingPermissions) {
      getRolesForPageTrigger({ companyId: currentUserState.companyId });
    }
  }, [currentUserState?.companyId, getRolesForPageTrigger, canViewRoleList, isLoadingPermissions]);

  const validateRoleName = (value: string) => {
    if (isFocused && value.trim() === '') {
      setRoleNameError('Rol Adı Boş Olamaz');
      return false;
    }
    setRoleNameError('');
    return true;
  };

  const handleRoleNameFocus = () => setIsFocused(true);
  const handleRoleNameBlur = () => validateRoleName(roleName);
  const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRoleName(value);
    if (isFocused) validateRoleName(value);
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setRoleName('');
    setIsChecker(false);
    setRoleNameError('');
    setIsFocused(false);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    if (!canCreateRole) {
      enqueueSnackbar('Rol ekleme yetkiniz bulunmamaktadır.', { variant: 'error' });
      return;
    }
    if (!validateRoleName(roleName) || !roleName.trim()) return;

    setLoading(true);
    try {
      const result = await addRole({
        roleName: roleName.trim(),
        companyName: currentUserState?.companyId?.toString() || '',
        checker: isChecker,
        maker: !isChecker,
        roles: []
      });

      if (result.data && result.data.success) {
        enqueueSnackbar('Rol Başarıyla Eklendi', { variant: 'success' });
        handleAddModalClose();
        if (currentUserState?.companyId && canViewRoleList) {
          getRolesForPageTrigger({ companyId: currentUserState.companyId });
        }
      } else {
        enqueueSnackbar(result.data?.message || 'Rol eklenirken bir hata oluştu.', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Rol eklenirken bir hata oluştu.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (roleId: string) => {
    const roleToEdit: any = rolesData?.data?.find(role => role.id === roleId);
    if (roleToEdit) {
      setEditingRole(roleToEdit);
      setEditRoleName(cleanRoleName(roleToEdit.name));
      setEditIsChecker(roleToEdit.checker || false);
      setEditIsMaker(roleToEdit.maker || false);
      setEditApprovalRoles(roleToEdit.roles || []);
    }
  };

  const handleEditModalClose = () => {
    setEditingRole(null);
    setEditRoleName('');
    setEditIsChecker(false);
    setEditIsMaker(false);
    setEditApprovalRoles([]);
  };

  const handleDeleteRole = (roleId: string) => {
    const role = rolesData?.data?.find(r => r.id === roleId);
    if (role) {
      setRoleToDelete(role);
      setDeleteConfirmOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    if (!canDeleteRole) {
      enqueueSnackbar('Rol silme yetkiniz bulunmamaktadır.', { variant: 'error' });
      return;
    }
    setDeleteLoading(true);
    try {
      const result = await removeRoleTrigger({ id: roleToDelete.id });
      if (result.data && result.data.success) {
        enqueueSnackbar('Rol Başarıyla Silindi', { variant: 'success' });
        setDeleteConfirmOpen(false);
        setRoleToDelete(null);
        if (currentUserState?.companyId && canViewRoleList) {
          getRolesForPageTrigger({ companyId: currentUserState.companyId });
        }
      } else {
        enqueueSnackbar(result.data?.message || 'Rol silinirken bir hata oluştu.', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Rol silinirken bir hata oluştu.', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingRole || !editRoleName.trim()) return;
    if (!canUpdateRole) {
      enqueueSnackbar('Rol güncelleme yetkiniz bulunmamaktadır.', { variant: 'error' });
      return;
    }
    setLoading(true);
    try {
      const fullRoleName = `CorporateCompany.${currentUserState?.companyId}.${editRoleName.trim()}`;
      const result = await updateRole({
        id: editingRole.id,
        roleName: fullRoleName,
        companyName: currentUserState?.companyId?.toString() || '',
        checker: editIsChecker,
        maker: editIsMaker,
        roles: editApprovalRoles
      });

      if (result.data && result.data.success) {
        enqueueSnackbar('Rol Başarıyla Güncellendi', { variant: 'success' });
        handleEditModalClose();
        if (currentUserState?.companyId && canViewRoleList) {
          getRolesForPageTrigger({ companyId: currentUserState.companyId });
        }
      } else {
        enqueueSnackbar(result.data?.message || 'Rol güncellenirken bir hata oluştu.', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Rol güncellenirken bir hata oluştu.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const cleanRoleName = (roleName: string) => {
    const regex = /^CorporateCompany\.\d+\./;
    return roleName.replace(regex, '');
  };

  // Yetkiler yüklenirken standart loader göster (erişim engellendi önce görünmesin)
   if (!isLoadingPermissions && !canViewRoleList) {
    return (
      <Container maxWidth={false} sx={{
        flexGrow: 1,
        padding: { xs: '110px 8px 16px 8px', sm: '110px 12px 24px 12px', md: '110px 32px 40px 32px', lg: '110px 40px 40px 40px' },
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper elevation={0} sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 500,
          textAlign: 'center',
          border: '1px solid rgba(252, 165, 165, 0.3)',
          background: 'rgba(255,255,255,0.9)'
        }}>
          <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>Erişim Engellendi</Typography>
          <Typography variant="body1" sx={{ color: '#7f1d1d', fontSize: '1rem' }}>
            Rol listesini görüntüleme yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Container
        maxWidth={false}
        sx={{
          flexGrow: 1,
          height: '100vh',
          maxHeight: '100vh',
          padding: { xs: '110px 8px 16px 8px', sm: '110px 12px 24px 12px', md: '110px 32px 40px 32px', lg: '110px 40px 40px 40px' },
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
            }
          }}
        >
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            py: { xs: 2, sm: 3, md: 4 },
            pb: 2,
            px: 0,
            background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
            borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
          }}>
            <Fade in={true} timeout={600}>
              <Box sx={{ px: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', maxWidth: 1200, mx: 'auto' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <RoleActions onAddRoleClick={() => setAddModalOpen(true)} canAddRole={canCreateRole} disableMargin />
                </Box>

                {/* Roller + liste tek cover Box */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    mt: { xs: 2, sm: 3 },
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    background: 'rgba(255,255,255,0.6)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Roller başlığı - Yetkilendirme Yönetimi ile aynı stil */}
                  <Box sx={{ flexShrink: 0, px: { xs: 3, sm: 4 }, pt: { xs: 2, sm: 2.5 }, pb: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                      <Avatar
                        sx={{
                          width: { xs: 36, sm: 44 },
                          height: { xs: 36, sm: 44 },
                          bgcolor: 'primary.main',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                        }}
                      >
                        <SecurityIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.75rem' },
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 0.5
                          }}
                        >
                          Roller
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                          {rolesData?.data?.length ?? 0} rol
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.1) }} />
                  </Box>

                  {/* Rol Listesi - cover içinde scroll */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'auto',
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1.5, sm: 2 }
                    }}
                  >
                  <Stack spacing={{ xs: 2, sm: 3 }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
                        <FintechLoader />
                      </Box>
                    ) : hasFetchedRoles && rolesData?.data?.length > 0 ? (
                      rolesData.data.map((role, index) => (
                        <WaveAnimation key={role.id} delay={index * 0.15} duration={0.8}>
                          <Card
                            sx={{
                              borderRadius: 3,
                              overflow: 'hidden',
                              background: 'rgba(255,255,255,0.8)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                              willChange: 'transform, box-shadow',
                              transition: 'all 0.25s ease-out',
                              '&:hover': {
                                transform: 'translateY(-8px) scale(1.005)',
                                boxShadow: '0 25px 60px rgba(59, 130, 246, 0.2)',
                                background: 'rgba(255,255,255,0.95)'
                              }
                            }}
                          >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                                  <Box sx={{
                                    width: { xs: 40, sm: 44 },
                                    height: { xs: 40, sm: 44 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <SecurityIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: 'white' }} />
                                  </Box>
                                  <Box>
                                    <Typography variant="h6" sx={{
                                      fontWeight: 600,
                                      mb: 0.5,
                                      fontSize: { xs: '0.95rem', sm: '1rem' },
                                      color: '#1e293b'
                                    }}>
                                      {cleanRoleName(role.name)}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                      opacity: 0.8,
                                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                      color: '#64748b'
                                    }}>
                                      {role.checker ? 'Onaylayıcı rol' : 'Standart rol'}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={0.75}>
                                  {canUpdateRole && (
                                    <IconButton
                                      size="medium"
                                      color="primary"
                                      onClick={() => handleEditRole(role.id)}
                                      sx={{
                                        borderRadius: 2,
                                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                                        '&:hover': {
                                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                          color: 'white'
                                        }
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                  {canDeleteRole && (
                                    <IconButton
                                      size="medium"
                                      color="error"
                                      onClick={() => handleDeleteRole(role.id)}
                                      sx={{
                                        borderRadius: 2,
                                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                                        '&:hover': { bgcolor: 'error.main', color: 'white' }
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </WaveAnimation>
                      ))
                    ) : hasFetchedRoles ? (
                      <Card
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          background: 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.06)'
                        }}
                      >
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Box
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: '50%',
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 2
                            }}
                          >
                            <GroupIcon sx={{ fontSize: 36 }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, fontSize: '1.125rem' }}>
                            Henüz rol bulunmuyor
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                            Yeni Rol Ekle butonu ile ilk rolünüzü ekleyin
                          </Typography>
                        </Box>
                      </Card>
                    ) : null}
                  </Stack>
                </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Paper>
      </Container>

      {/* Add Role Modal */}
      <ConfirmationModal
        isOpen={addModalOpen}
        onClose={handleAddModalClose}
        onConfirm={() => handleSubmit()}
        title="Yeni Rol Ekle"
        confirmButtonText={loading ? 'Ekleniyor...' : 'Ekle'}
        closeButtonText="İptal"
        isLoading={loading}
        optionalWidth="760px"
        confirmButtonDisabled={!roleName.trim() || !!roleNameError || !canCreateRole}
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          titleColor: 'white',
          icon: (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </Box>
          )
        }}
      >
        <Box sx={{ py: 1 }}>
          <TextField
            fullWidth
            label="Rol Adı"
            value={roleName}
            onChange={handleRoleNameChange}
            onFocus={handleRoleNameFocus}
            onBlur={handleRoleNameBlur}
            required
            variant="outlined"
            placeholder="Örnek: Muhasebe Uzmanı"
            error={!!roleNameError}
            helperText={roleNameError}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <SecurityIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isChecker}
                onChange={(e) => setIsChecker(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body1" sx={{ fontSize: '0.9375rem' }}>İşlemleri onaylayacak rol (Checker)</Typography>}
          />
        </Box>
      </ConfirmationModal>

      {/* Edit Role Modal */}
      <ConfirmationModal
        isOpen={!!editingRole}
        onClose={handleEditModalClose}
        onConfirm={handleEditSubmit}
        title="Rol Düzenle"
        confirmButtonText={loading ? 'Güncelleniyor...' : 'Güncelle'}
        closeButtonText="İptal"
        isLoading={loading}
        optionalWidth="760px"
        confirmButtonDisabled={!editRoleName.trim() || !canUpdateRole}
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          titleColor: 'white',
          icon: (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </Box>
          )
        }}
      >
        <Box sx={{ py: 1 }}>
          <TextField
            fullWidth
            label="Rol Adı"
            value={editRoleName}
            onChange={(e) => setEditRoleName(e.target.value)}
            variant="outlined"
            placeholder="Rol adını giriniz"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <SecurityIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary', fontSize: '1rem' }}>Yetki Durumu</Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editIsChecker}
                  onChange={(e) => {
                    setEditIsChecker(e.target.checked);
                    if (!e.target.checked) setEditApprovalRoles([]);
                  }}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body1" sx={{ fontSize: '0.9375rem' }}>İşlemleri onaylayacak rol (Checker)</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editIsMaker}
                  onChange={(e) => setEditIsMaker(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body1" sx={{ fontSize: '0.9375rem' }}>Tüm işlemleri onaylanacak rol (Maker)</Typography>}
            />
          </Stack>
          {editIsChecker && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary', fontSize: '1rem' }}>Onaylanacak Roller</Typography>
              <Autocomplete
                multiple
                size="small"
                options={rolesData?.data || []}
                value={rolesData?.data?.filter(role => editApprovalRoles.includes(role.id)) || []}
                onChange={(_, newValue) => setEditApprovalRoles(newValue.map(role => role.id))}
                getOptionLabel={(option) => cleanRoleName(option.name)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField {...params} placeholder="Rolleri seçiniz" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={cleanRoleName(option.name)}
                      size="small"
                      sx={{
                        mr: 0.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-label': { color: 'white' },
                        '& .MuiChip-deleteIcon': { fontSize: 16, color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }
                      }}
                    />
                  ))
                }
              />
            </>
          )}
        </Box>
      </ConfirmationModal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setRoleToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        title="Rol Silme Onayı"
        confirmButtonText="Sil"
        closeButtonText="İptal"
        isLoading={deleteLoading}
        optionalWidth="580px"
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          titleColor: 'white',
          icon: (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WarningIcon sx={{ fontSize: 18 }} />
            </Box>
          )
        }}
      >
        <Box sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontSize: '1rem', mb: 1 }}>
            <strong>{roleToDelete ? cleanRoleName(roleToDelete.name) : ''}</strong> rolünü silmek istediğinize emin misiniz?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bu işlem geri alınamaz ve bu role sahip tüm kullanıcılar etkilenecektir.
          </Typography>
        </Box>
      </ConfirmationModal>
    </>
  );
}
